import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { usePillars } from "@/contexts/PillarsContext";
import { BG_STYLES } from "@/pages/WelcomePage";
import CoverAnimation from "@/components/CoverAnimation";
import { UploadSimple, Trash, FloppyDisk, Eye, ArrowRight, Lightning } from "@phosphor-icons/react";

const BG_OPTIONS = [
  { id: "granite", label: "Granite", swatch: "linear-gradient(135deg, #CDD1D7 0%, #A9AEB6 100%)" },
  { id: "warm", label: "Warm Glow", swatch: "linear-gradient(135deg, #FFB450 0%, #CCFF00 100%)" },
  { id: "cool", label: "Cool Mist", swatch: "linear-gradient(135deg, #508CFF 0%, #00E5FF 100%)" },
  { id: "forest", label: "Forest", swatch: "linear-gradient(135deg, #50FF8C 0%, #CCFF00 100%)" },
  { id: "sunset", label: "Sunset", swatch: "linear-gradient(135deg, #FF5A8C 0%, #FFB43C 100%)" },
  { id: "midnight", label: "Midnight", swatch: "linear-gradient(135deg, #8C50FF 0%, #CCFF00 100%)" },
  { id: "minimal", label: "Minimal", swatch: "linear-gradient(135deg, #FFFFFF 0%, #DCDEE2 100%)" },
];

const ANIM_OPTIONS = [
  { id: "none", label: "None", desc: "Static background — fastest, fully calm." },
  { id: "particles", label: "Drifting particles", desc: "Soft dots rising slowly across the cover." },
  { id: "parallax", label: "Parallax gradient", desc: "Gently morphing color blobs in the background." },
];

export default function BrandingPanel() {
  const { settings, refresh } = usePillars();
  const [form, setForm] = useState({
    cover_title: settings.cover_title || "",
    cover_mission: settings.cover_mission || "",
    cover_mission_bold: !!settings.cover_mission_bold,
    cover_button_label: settings.cover_button_label || "Log in",
    logo_url: settings.logo_url || "",
    cover_background_url: settings.cover_background_url || "",
    cover_background_style: settings.cover_background_style || "warm",
    cover_animation: settings.cover_animation || "none",
    currency: settings.currency || "CAD",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);
  const logoInputRef = useRef(null);
  const bgInputRef = useRef(null);
  const appIconInputRef = useRef(null);
  const [appIconBusy, setAppIconBusy] = useState(false);
  const [appIconVer, setAppIconVer] = useState(Date.now());
  const [hasCustomIcon, setHasCustomIcon] = useState(!!settings.app_icon);

  const uploadAppIcon = async (file) => {
    setAppIconBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/branding/app-icon", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHasCustomIcon(true);
      setAppIconVer(Date.now());
    } catch (e) { setErr(formatApiError(e)); }
    finally { setAppIconBusy(false); if (appIconInputRef.current) appIconInputRef.current.value = ""; }
  };

  const resetAppIcon = async () => {
    setAppIconBusy(true); setErr("");
    try {
      await api.delete("/admin/branding/app-icon");
      setHasCustomIcon(false);
      setAppIconVer(Date.now());
    } catch (e) { setErr(formatApiError(e)); }
    finally { setAppIconBusy(false); }
  };

  useEffect(() => {
    setForm({
      cover_title: settings.cover_title || "",
      cover_mission: settings.cover_mission || "",
      cover_mission_bold: !!settings.cover_mission_bold,
      cover_button_label: settings.cover_button_label || "Log in",
      logo_url: settings.logo_url || "",
      cover_background_url: settings.cover_background_url || "",
      cover_background_style: settings.cover_background_style || "warm",
      cover_animation: settings.cover_animation || "none",
      currency: settings.currency || "CAD",
    });
  }, [settings]);

  const upload = async (file, key) => {
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/settings/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, [key]: data.url }));
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true); setErr(""); setSaved(false);
    try {
      // Note: empty string means "unset" → send null so the welcome page falls back to defaults
      const payload = {};
      ["cover_title", "cover_mission", "cover_mission_bold", "cover_button_label", "logo_url", "cover_background_url", "cover_background_style", "cover_animation", "currency"].forEach((k) => {
        payload[k] = form[k] === "" ? null : form[k];
      });
      await api.patch("/admin/settings", payload);
      await refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // Live preview style
  const previewStyle = form.cover_background_url
    ? { backgroundImage: `linear-gradient(rgba(220,222,226,0.55), rgba(220,222,226,0.85)), url(${form.cover_background_url})`, backgroundSize: "cover", backgroundPosition: "center" }
    : (BG_STYLES[form.cover_background_style] || BG_STYLES.warm);

  return (
    <div className="flex flex-col gap-5">
      <div className="card">
        <h2 className="text-xl font-bold font-display mb-1">Welcome cover branding</h2>
        <p className="text-[#000000] text-sm mb-5">
          This is what visitors see at the front door before they log in. Keep it warm and inviting.
        </p>

        {err && <div className="text-[#FF5100] mb-3 text-sm" data-testid="branding-error">{err}</div>}

        {/* App icon (PWA home-screen / favicon) */}
        <div className="mb-5">
          <div className="text-xs uppercase tracking-[0.15em] text-[#000000] mb-2">App icon (home screen &amp; browser tab)</div>
          <div className="flex items-center gap-4 flex-wrap">
            <img
              key={appIconVer}
              src={`${process.env.REACT_APP_BACKEND_URL}/api/branding/apple-touch-icon.png?v=${appIconVer}`}
              alt="App icon"
              className="w-20 h-20 rounded-2xl object-contain"
              style={{ border: "1px solid #C4C8CE" }}
              data-testid="app-icon-preview"
            />
            <input
              ref={appIconInputRef}
              type="file"
              hidden
              accept=".png,.jpg,.jpeg,.webp"
              onChange={(e) => e.target.files?.[0] && uploadAppIcon(e.target.files[0])}
              data-testid="app-icon-file-input"
            />
            <button
              className="btn-secondary flex items-center gap-1"
              onClick={() => appIconInputRef.current?.click()}
              disabled={appIconBusy}
              data-testid="upload-app-icon-button"
            >
              <UploadSimple size={16} weight="bold" /> {appIconBusy ? "Working…" : "Upload logo for icon"}
            </button>
            {hasCustomIcon && (
              <button
                className="btn-ghost text-[#FF5100] flex items-center gap-1"
                onClick={resetAppIcon}
                disabled={appIconBusy}
                data-testid="reset-app-icon-button"
              >
                <Trash size={16} /> Reset to default
              </button>
            )}
          </div>
          <div className="text-xs text-[#94979C] mt-1.5">
            Your logo gets centered on the lime rounded square automatically. Transparent PNGs look best.
            Already-installed home-screen icons can take a day or a reinstall to refresh.
          </div>
        </div>

        {/* Logo */}
        <div className="mb-5">
          <div className="text-xs uppercase tracking-[0.15em] text-[#000000] mb-2">Logo</div>
          <div className="flex items-center gap-4 flex-wrap">
            <div
              className="w-20 h-20 rounded-2xl grid place-items-center"
              style={{ background: "#DCDEE2", border: "1px solid #C4C8CE" }}
              data-testid="logo-preview"
            >
              {form.logo_url ? (
                <img src={form.logo_url} alt="Logo" className="max-h-16 max-w-[160px] w-auto h-auto object-contain" />
              ) : (
                <div className="text-3xl font-display font-black" style={{ color: "#CCFF00" }}>E</div>
              )}
            </div>
            <input
              ref={logoInputRef}
              type="file"
              hidden
              accept=".png,.jpg,.jpeg,.webp,.svg,.gif"
              onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "logo_url")}
              data-testid="logo-file-input"
            />
            <button
              className="btn-secondary flex items-center gap-1"
              onClick={() => logoInputRef.current?.click()}
              disabled={busy}
              data-testid="upload-logo-button"
            >
              <UploadSimple size={16} weight="bold" /> Upload logo
            </button>
            {form.logo_url && (
              <button
                className="btn-ghost text-[#FF5100] flex items-center gap-1"
                onClick={() => setForm({ ...form, logo_url: "" })}
                data-testid="remove-logo-button"
              >
                <Trash size={16} /> Remove
              </button>
            )}
            <input
              type="text"
              className="input-base flex-1 min-w-[200px]"
              placeholder="…or paste an image URL"
              value={form.logo_url}
              onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
              data-testid="logo-url-input"
            />
          </div>
        </div>

        {/* Background */}
        <div className="mb-5">
          <div className="text-xs uppercase tracking-[0.15em] text-[#000000] mb-2">Background style</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
            {BG_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setForm({ ...form, cover_background_style: opt.id, cover_background_url: "" })}
                data-testid={`bg-style-${opt.id}`}
                className="rounded-xl p-3 flex items-center gap-2 transition-all text-left"
                style={{
                  background: "#DCDEE2",
                  border: "1px solid " + (form.cover_background_style === opt.id && !form.cover_background_url ? "#CCFF00" : "#C4C8CE"),
                  outline: form.cover_background_style === opt.id && !form.cover_background_url ? "2px solid rgba(204,255,0,0.4)" : "none",
                  cursor: "pointer",
                }}
              >
                <div className="w-10 h-10 rounded-lg" style={{ background: opt.swatch }} />
                <div className="text-sm font-semibold">{opt.label}</div>
              </button>
            ))}
          </div>
          <div className="text-xs uppercase tracking-[0.15em] text-[#000000] mb-2 mt-4">…or upload a custom background image</div>
          <div className="flex items-center gap-2 flex-wrap">
            {form.cover_background_url && (
              <div
                className="w-32 h-16 rounded-lg bg-cover bg-center"
                style={{ backgroundImage: `url(${form.cover_background_url})`, border: "1px solid #C4C8CE" }}
                data-testid="bg-preview"
              />
            )}
            <input
              ref={bgInputRef}
              type="file"
              hidden
              accept=".png,.jpg,.jpeg,.webp,.svg,.gif"
              onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "cover_background_url")}
              data-testid="bg-file-input"
            />
            <button
              className="btn-secondary flex items-center gap-1"
              onClick={() => bgInputRef.current?.click()}
              disabled={busy}
              data-testid="upload-bg-button"
            >
              <UploadSimple size={16} weight="bold" /> Upload image
            </button>
            {form.cover_background_url && (
              <button
                className="btn-ghost text-[#FF5100] flex items-center gap-1"
                onClick={() => setForm({ ...form, cover_background_url: "" })}
                data-testid="remove-bg-button"
              >
                <Trash size={16} /> Use style preset instead
              </button>
            )}
          </div>
        </div>

        {/* Animation */}
        <div className="mb-5">
          <div className="text-xs uppercase tracking-[0.15em] text-[#000000] mb-2">Background animation</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {ANIM_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setForm({ ...form, cover_animation: opt.id })}
                data-testid={`anim-${opt.id}`}
                className="rounded-xl p-3 transition-all text-left"
                style={{
                  background: "#DCDEE2",
                  border: "1px solid " + (form.cover_animation === opt.id ? "#CCFF00" : "#C4C8CE"),
                  outline: form.cover_animation === opt.id ? "2px solid rgba(204,255,0,0.4)" : "none",
                  cursor: "pointer",
                }}
              >
                <div className="text-sm font-semibold mb-0.5">{opt.label}</div>
                <div className="text-xs text-[#000000] leading-snug">{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Copy */}
        <div className="grid grid-cols-1 gap-3 mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-[#000000] mb-1">Headline</div>
            <input
              className="input-base w-full text-lg font-display"
              maxLength={80}
              value={form.cover_title}
              onChange={(e) => setForm({ ...form, cover_title: e.target.value })}
              placeholder="The Self-Agency Project"
              data-testid="cover-title-input"
            />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-[#000000] mb-1">Mission statement</div>
            <textarea
              className="input-base w-full min-h-[100px] leading-relaxed"
              maxLength={400}
              value={form.cover_mission}
              onChange={(e) => setForm({ ...form, cover_mission: e.target.value })}
              placeholder="A short, warm line that makes people want to start."
              data-testid="cover-mission-input"
            />
            <div className="text-xs text-[#000000] mt-1 text-right">{form.cover_mission.length}/400</div>
            <label className="flex items-center gap-2 text-sm text-[#000000] mt-1 cursor-pointer">
              <input
                type="checkbox"
                checked={form.cover_mission_bold}
                onChange={(e) => setForm({ ...form, cover_mission_bold: e.target.checked })}
                data-testid="cover-mission-bold-toggle"
              />
              <span className="font-bold">Bold</span> mission statement
            </label>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-[#000000] mb-1">Button label</div>
            <input
              className="input-base w-full max-w-xs"
              maxLength={40}
              value={form.cover_button_label}
              onChange={(e) => setForm({ ...form, cover_button_label: e.target.value })}
              placeholder="Log in"
              data-testid="cover-button-input"
            />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-[#000000] mb-1">Store currency</div>
            <select
              className="input-base w-full max-w-xs"
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
              data-testid="currency-select"
            >
              <option value="CAD">CAD — Canadian dollars</option>
              <option value="USD">USD — US dollars</option>
            </select>
            <div className="text-xs text-[#000000] mt-1">Used for public course prices and Stripe checkout.</div>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <button
            className="btn-primary flex items-center gap-2"
            disabled={busy}
            onClick={save}
            data-testid="save-branding-button"
          >
            <FloppyDisk size={16} weight="bold" /> {busy ? "Saving…" : "Save branding"}
          </button>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary flex items-center gap-2"
            data-testid="open-welcome-link"
          >
            <Eye size={16} weight="bold" /> Open welcome page
          </a>
          {saved && <span className="text-[#10B981] text-sm" data-testid="branding-saved">Saved ✓</span>}
        </div>
      </div>

      {/* Live preview */}
      <div className="card overflow-hidden p-0">
        <div className="px-5 py-3 border-b border-[#C4C8CE] text-xs uppercase tracking-[0.15em] text-[#000000]">
          Live preview
        </div>
        <div
          className="w-full p-12 flex flex-col items-center text-center relative overflow-hidden"
          style={{ ...previewStyle, minHeight: "380px" }}
          data-testid="cover-preview"
        >
          <CoverAnimation kind={form.cover_animation} compact />
          <div style={{ position: "relative", zIndex: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
            {form.logo_url ? (
              <img src={form.logo_url} alt="Logo" className="max-h-20 max-w-[220px] w-auto h-auto mb-6 object-contain" />
            ) : (
              <div
                className="w-16 h-16 rounded-2xl grid place-items-center font-display font-black text-3xl mb-6"
                style={{ background: "linear-gradient(135deg, #CCFF00 0%, #B8E600 100%)", color: "#000000" }}
              >E</div>
            )}
            <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.05] mb-4" style={{ color: "#000000" }}>
              {form.cover_title || "The Self-Agency Project"}
            </h2>
            <p className="text-base md:text-lg max-w-xl mb-7" style={{ color: "#000000", fontWeight: form.cover_mission_bold ? 800 : undefined }}>
              {form.cover_mission || "Your mission statement appears here."}
            </p>
            <button
              type="button"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full font-display font-bold transition-all"
              style={{
                background: "#CCFF00", color: "#000000",
                boxShadow: "0 10px 40px rgba(204,255,0,0.35)",
                cursor: "pointer", border: "none",
              }}
            >
              <Lightning size={18} weight="fill" />
              {form.cover_button_label || "Log in"}
              <ArrowRight size={18} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
